import { Link } from "react-router-dom";
import logo from "../assets/img/logo.png";

export default function Footer() {
  return (
    <footer className="wb-footer">
      <style>{`
        .wb-footer {
          background: var(--surface);
          border-top: 1px solid var(--border);
          padding: 64px 48px 32px;
          position: relative;
          z-index: 10;
          width: 100%;
        }

        .wb-footer-container {
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
        }

        .wb-footer-grid {
          display: grid;
          grid-template-columns: 2.2fr 1fr 1fr 1fr;
          gap: 40px;
          margin-bottom: 48px;
        }

        .wb-footer-logo {
          font-family: 'Orbitron', monospace;
          font-size: 20px;
          font-weight: 900;
          color: var(--neon);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }

        .wb-footer-logo-img {
          height: 38px;
          width: auto;
          object-fit: contain;
        }

        .wb-footer-tagline {
          color: var(--muted);
          font-size: 14px;
          line-height: 1.7;
          margin-top: 14px;
          max-width: 320px;
        }

        .wb-footer-socials {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }

        .wb-footer-social {
          width: 38px;
          height: 38px;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--surface2);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--muted);
          text-decoration: none;
          font-size: 16px;
          transition: all 0.25s ease;
        }

        .wb-footer-social:hover {
          border-color: var(--neon);
          color: var(--neon);
          box-shadow: 0 0 14px rgba(0, 245, 196, 0.3);
          transform: translateY(-2px);
        }

        .wb-footer-col-title {
          font-family: 'Orbitron', monospace;
          font-size: 12px;
          font-weight: 700;
          color: var(--text);
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 20px;
        }

        .wb-footer-col-link {
          display: block;
          color: var(--muted);
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 12px;
          transition: color 0.2s ease, transform 0.2s ease;
        }

        .wb-footer-col-link:hover {
          color: var(--neon);
          transform: translateX(3px);
        }

        .wb-footer-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 28px;
          border-top: 1px solid var(--border);
          flex-wrap: wrap;
          gap: 16px;
        }

        .wb-footer-copy {
          color: var(--muted);
          font-size: 13px;
        }

        .wb-footer-bottom-links {
          display: flex;
          gap: 20px;
        }

        .wb-footer-bottom-link {
          color: var(--muted);
          text-decoration: none;
          font-size: 13px;
          transition: color 0.2s ease;
        }

        .wb-footer-bottom-link:hover {
          color: var(--neon);
        }

        /* Tablet (768px - 1024px) */
        @media (max-width: 1024px) and (min-width: 641px) {
          .wb-footer {
            padding: 48px 32px 28px;
          }
          .wb-footer-grid {
            grid-template-columns: 1fr 1fr;
            row-gap: 36px;
            column-gap: 28px;
          }
          .wb-footer-brand {
            grid-column: 1 / -1;
          }
          .wb-footer-tagline {
            max-width: 480px;
          }
        }

        /* Mobile (< 640px) */
        @media (max-width: 640px) {
          .wb-footer {
            padding: 40px 20px 24px;
          }
          .wb-footer-grid {
            grid-template-columns: 1fr;
            row-gap: 32px;
            margin-bottom: 36px;
          }
          .wb-footer-bottom {
            flex-direction: column;
            align-items: flex-start;
            gap: 14px;
          }
          .wb-footer-bottom-links {
            gap: 16px;
            flex-wrap: wrap;
          }
        }
      `}</style>

      <div className="wb-footer-container">
        <div className="wb-footer-grid">
          <div className="wb-footer-brand">
            <Link to="/" className="wb-footer-logo">
              <img src={logo} alt="WalkBattle Logo" className="wb-footer-logo-img" />
              <span>WalkBattle</span>
            </Link>
            <p className="wb-footer-tagline">
              Own Your Path. Rule Your City. Turn every step into victory with the most competitive fitness platform ever built.
            </p>
            <div className="wb-footer-socials">
              {[
                { icon: "𝕏", url: "/" },
                { icon: "📸", url: "/" },
                { icon: "▶", url: "/" },
                { icon: "💬", url: "/" },
              ].map(({ icon, url }, i) => (
                <a
                  key={i}
                  href={url}
                  className="wb-footer-social"
                  aria-label="Social Link"
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="wb-footer-col-title">Product</div>
            {[
              ["Features", "/features"],
              ["Leaderboard", "/features"],
              ["Battles", "/features"],
              ["Territories", "/features"],
              ["Rewards", "/features"],
            ].map(([l, h]) => (
              <Link key={l} to={h} className="wb-footer-col-link">
                {l}
              </Link>
            ))}
          </div>

          <div>
            <div className="wb-footer-col-title">Company</div>
            {[
              ["About", "/about"],
              ["Contact", "/contact"],
              ["FAQ", "/faq"],
              ["Support", "/support"],
            ].map(([l, h]) => (
              <Link key={l} to={h} className="wb-footer-col-link">
                {l}
              </Link>
            ))}
          </div>

          <div>
            <div className="wb-footer-col-title">Legal</div>
            {[
              ["Privacy Policy", "/privacy"],
              ["Terms of Service", "/terms"],
              ["Cookie Policy", "/cookies"],
              ["Refund Policy", "/refund"],
              ["Delete Data", "/delete-data"],
            ].map(([l, h]) => (
              <Link key={l} to={h} className="wb-footer-col-link">
                {l}
              </Link>
            ))}
          </div>
        </div>

        <div className="wb-footer-bottom">
          <p className="wb-footer-copy">© 2026 WalkBattle. All rights reserved. Built with 🔥</p>
          <div className="wb-footer-bottom-links">
            <Link to="/privacy" className="wb-footer-bottom-link">Privacy</Link>
            <Link to="/terms" className="wb-footer-bottom-link">Terms</Link>
            <Link to="/cookies" className="wb-footer-bottom-link">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
